import React, { useState, useEffect, useRef } from 'react';
import Lottie, { LottieRefCurrentProps } from 'lottie-react';
import Sound from 'react-sound';
import moment from 'moment';
import confetti from '../../assets/animations/top-down-confetti.json';
import giftbox from '../../assets/animations/giftbox-unbox.json';
import ReactMarkdown from 'react-markdown';

import { makeStyles } from '@material-ui/core/styles';
import Typography from '@material-ui/core/Typography';
import { SvgIcon, IconButton } from '@material-ui/core';
import CloseIcon from '@material-ui/icons/Close';
import { ReactComponent as CheckIcon } from '../../assets/icons/Check.svg';
import { ReactComponent as CelebrateIcon } from '../../assets/icons/Celebration.svg';
import FullScreenDialog, { FullScreenDialogProps } from '../FullScreenDialog';

import * as ProfileService from '../../api/profileService';
import * as StampCampaignService from '../../api/stampCampaignService';

const useStyles = makeStyles((theme) => ({
  cardContainer: {
    overflow: 'hidden',
    marginTop: theme.spacing(15),
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
  },
  title: {
    fontWeight: theme.typography.fontWeightBold,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    textAlign: 'center',
  },
  celebrateText: {
    fontWeight: theme.typography.fontWeightBold,
    color: theme.palette.text.secondary,
    marginTop: theme.spacing(2),
    textAlign: 'center',
  },
  vip: {
    fontWeight: theme.typography.fontWeightBold,
    color: '#ffae23',
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  fillRoot: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    borderWidth: 2,
    borderStyle: 'solid',
    borderColor: theme.palette.primary.main,
    width: 60,
    height: 60,
    borderRadius: 60 / 2,
    backgroundColor: theme.palette.primary.main,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  celebrateRoot: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    width: 200,
    height: 100,
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
  },
  icon: {
    color: theme.palette.primary.main,
  },
  timestamp: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    textAlign: 'center',
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
  },
  box: {
    flex: 1,
    display: 'flex',
    justifyContent: 'center',
  },
  close: {
    marginLeft: 'auto',
  },
  closeIcon: {
    color: theme.palette.common.black,
  },
  moreStampContainer: {
    marginTop: theme.spacing(),
    paddingLeft: theme.spacing(2),
    paddingRight: theme.spacing(2),
    backgroundColor: '#fafafa',
    borderRadius: 10,
    cursor: 'pointer',
    maxWidth: 300,
  },
  lottieGiftbox: {
    position: 'absolute',
    left: 0,
    right: 0,
    top: 0,
    bottom: 0,
    margin: 'auto',
    width: 500,
    height: 500,
    maxWidth: '100%',
    maxHeight: '100%',
  },
  markdown: {
    marginTop: theme.spacing(2),
    marginBottom: theme.spacing(2),
    fontFamily: 'ClarikaGeometric',
    fontSize: 14,
    fontWeight: 400,
    paddingLeft: theme.spacing(4),
    paddingRight: theme.spacing(4),
  },
}));

type Props = Pick<FullScreenDialogProps, 'open' | 'onClose'> & {
  campaignId: string;
  quantity: number;
  isMultipleStamp: boolean;
  stampAdded: string;
  handleAddMoreStamp: () => void;
  allowAddMoreStamp: boolean;
  isMilestone: boolean;
  isComplete: boolean;
};

export default function AddStampSuccessDialog({
  onClose,
  open,
  campaignId,
  quantity,
  isMultipleStamp,
  stampAdded,
  handleAddMoreStamp,
  allowAddMoreStamp,
  isMilestone,
  isComplete,
}: Props) {
  const isAndroid = /Android/i.test(navigator.userAgent);
  const classes = useStyles();
  const [profile, setProfile] = useState<ProfileService.Profile | null>(null);
  const [stampCampaign, setStampCampaign] =
    useState<StampCampaignService.StampCampaign | null>(null);
  const [isPlaying, setIsPlaying] = useState(isAndroid);
  const appleRedirectURI = process.env.REACT_APP_APPLE_REDIRECT_URI;
  const [isSoundPlaying, setIsSoundPlaying] = useState(isAndroid);
  const [loadCompletedAnimation, setLoadCompletedAnimation] =
    useState(isMilestone);
  const lottieRef = useRef<LottieRefCurrentProps>(null);

  useEffect(() => {
    let init = false;
    const fetchProfile = async () => {
      const res = await ProfileService.get();
      if (!init) {
        setProfile(res);
      }
    };

    fetchProfile();
    return () => {
      init = true;
    };
  }, []);

  useEffect(() => {
    const fetchStampCampaign = async () => {
      const res = await StampCampaignService.get(campaignId);
      setStampCampaign(res);
    };
    fetchStampCampaign();
  }, [campaignId]);

  useEffect(() => {
    if (loadCompletedAnimation) {
      setTimeout(() => setLoadCompletedAnimation(false), 1000);
    }
  }, [loadCompletedAnimation]);

  if (!profile) {
    return null;
  }

  if (!stampCampaign) {
    return null;
  }

  return (
    <FullScreenDialog open={open}>
      {loadCompletedAnimation && (
        <Lottie
          animationData={giftbox}
          loop={loadCompletedAnimation}
          autoplay={false}
          className={classes.lottieGiftbox}
          lottieRef={lottieRef}
          onDOMLoaded={() => lottieRef.current?.setSpeed(5)}
        />
      )}
      <>
        {!loadCompletedAnimation && (
          <>
            {isPlaying && (
              <Lottie
                animationData={confetti}
                loop={false}
                autoplay={true}
                style={{
                  left: 0,
                  right: 0,
                  position: 'absolute',
                }}
              />
            )}
            {isSoundPlaying && (
              <Sound
                url={
                  isMultipleStamp
                    ? `${appleRedirectURI}/sound/double-ding.mp3`
                    : `${appleRedirectURI}/sound/ding.mp3`
                }
                playStatus={isSoundPlaying ? 'PLAYING' : 'STOPPED'}
                onFinishedPlaying={() => {
                  setIsSoundPlaying(false);
                  setIsPlaying(false);
                }}
              />
            )}
            <div className={classes.box}>
              <div className={classes.close}>
                <IconButton
                  onClick={() => {
                    if (onClose) {
                      onClose({}, 'backdropClick');
                    }
                  }}
                >
                  <CloseIcon className={classes.closeIcon} />
                </IconButton>
              </div>
            </div>
            <div className={classes.cardContainer}>
              <Typography variant="h4" className={classes.title} gutterBottom>
                {profile.firstName} {profile.lastName}
              </Typography>
              <div className={classes.fillRoot}>
                <SvgIcon
                  component={CheckIcon}
                  color="primary"
                  className={classes.icon}
                  viewBox={'0 0 30 30'}
                  fontSize="large"
                />
              </div>
              {stampCampaign.type === 'passport' && isComplete ? (
                <>
                  <ReactMarkdown className={classes.markdown}>
                    {stampCampaign.notification}
                  </ReactMarkdown>
                  <Typography
                    variant="body1"
                    className={classes.timestamp}
                    gutterBottom
                  >
                    {moment().format('llll')}
                  </Typography>
                </>
              ) : (
                <>
                  <Typography
                    variant="h4"
                    className={classes.title}
                    gutterBottom
                  >
                    {isMilestone
                      ? 'You have received a reward'
                      : `${stampAdded} Stamp${
                          stampAdded === '1' ? '' : 's'
                        } Collected`}
                  </Typography>
                  <Typography
                    variant="body1"
                    className={classes.timestamp}
                    gutterBottom
                  >
                    {moment().format('llll')}
                  </Typography>
                  {/* {allowAddMoreStamp && (
                  <div
                    className={classes.moreStampContainer}
                    onClick={handleAddMoreStamp}
                  >
                    <Typography
                      variant="body1"
                      className={classes.timestamp}
                      gutterBottom
                    >
                      {!isMultipleStamp
                        ? 'To collect more than 1 stamp, please pass your device to a staff member'
                        : 'To collect more stamps, please pass your device to a staff member'}
                    </Typography>
                  </div>
                )} */}
                </>
              )}
              {!isAndroid && (
                <div
                  className={classes.celebrateRoot}
                  onClick={() => {
                    setIsSoundPlaying(true);
                    setIsPlaying(true);
                  }}
                >
                  <SvgIcon
                    component={CelebrateIcon}
                    color="primary"
                    className={classes.icon}
                    viewBox={'0 0 512 512'}
                    fontSize="large"
                  />
                  <Typography
                    variant="body1"
                    className={classes.celebrateText}
                    gutterBottom
                  >
                    Tap to Celebrate!
                  </Typography>
                </div>
              )}
            </div>
          </>
        )}
      </>
    </FullScreenDialog>
  );
}
